// React imports
import { useState, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';

// Generic imports
import _ from 'lodash';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

// Material UI imports
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Chip from '@mui/material/Chip';
import { alpha, useTheme } from '@mui/material/styles';
import { grey, green, purple, amber, cyan, red, blue } from '@mui/material/colors';

// Font Awesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChartMixed, faFileInvoice, faEnvelope, faDollarSign, faChartLineUp, faClock, faFileLines, faTrophy, faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { 
    faChartLineUp as faChartLineUpRegular, faChartLineDown as faChartLineDownRegular,
} from '@fortawesome/pro-solid-svg-icons';

// Local imports
import Header from '../../Header.js';
import OpportunitiesFilter, { applyFilter } from '../components/OpportunitiesFilter.js';
import { calcChargeableWeight, calcWeightOrMeasurment } from '../../CalcWeight.js';
import { opportunitySellAndCost, offerChoiceOf } from '../../utils/opportunityFinancials.js';
import currencyToText from '../../CurrencyToText.js';
import { LOST_REASONS } from '../../../schema/lostReasons.js';
import readableId from '../../ReadableId.js';
import { statusToText } from '../../OpportunityRow.utils.js';

import isoWeek from 'dayjs/plugin/isoWeek';
dayjs.extend(advancedFormat);
dayjs.extend(isoWeek);

// ─── Shared helpers ───

function transportModeLabel(o) {
    const tm = o.transportationMethod;
    if (tm === 'air') return 'Air';
    if (tm === 'land') return 'Land';
    // Sea: check products for FCL vs LCL
    const products = o.products || [];
    const hasLCL = products.some(p => (p.type || '').toLowerCase().includes('volume') || (p.type || '').toLowerCase().includes('weight') || (p.type || '').toLowerCase().includes('lcl'));
    if (hasLCL) return 'LCL';
    return 'FCL';
}

function getWonDate(opportunity) {
    // Most recent time the deal was won — either marked completed OR accepted by customer.
    // Must mirror the actions getFirstWonDate uses; otherwise accepted-by-customer deals
    // (which never log 'marked-completed') fall through to the fallback below.
    const log = (opportunity.log || []).filter(l => l.action === 'marked-completed' || l.action === 'accepted');
    if (log.length > 0) return log[log.length - 1].timestamp;
    // Fallback for won deals with no won-event log: use the stored first won date, then creation.
    // Never use `modified` — that tracks the last EDIT, so any change would drift "last won" to today.
    if (opportunity.status === 'completed' || opportunity.status === 'endcustomer-accepted') return opportunity.firstWonDate || opportunity.created;
    return null;
}

function getFirstWonDate(opportunity) {
    // Use stored firstWonDate if available
    if (opportunity.firstWonDate) return opportunity.firstWonDate;
    // Fallback: find the earliest 'marked-completed' or 'accepted' log entry
    const log = (opportunity.log || []).filter(l => l.action === 'marked-completed' || l.action === 'accepted');
    if (log.length > 0) return log[0].timestamp;
    // Final fallback: use the last won date
    return getWonDate(opportunity);
}

// Sell and cost per quote come from opportunitySellAndCost, the same figures as the quote's own
// summary bar: at the exchange rates the quote was priced at, without fields the customer never sees.
function calcRevenueAndCosts({ opportunities, exchangeRates, organization }) {
    const fraction = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    let totalRevenue = 0;
    let totalCosts = 0;

    for (const o of opportunities) {
        const fin = opportunitySellAndCost({ opportunity: o, exchangeRates, organization });
        if (!fin) continue;
        if (isFinite(fin.sell)) totalRevenue += fin.sell;
        if (isFinite(fin.cost)) totalCosts += fin.cost;
    }
    const gp = totalRevenue - totalCosts;
    const margin = totalRevenue > 0 ? (gp / totalRevenue) * 100 : 0;

    return { revenue: totalRevenue, costs: totalCosts, gp, margin, format: fraction };
}

function QuotationsAnalytics(props) {
    const { org, opportunities, me, accounts, exchangeRates } = useOutletContext();
    const navigate = useNavigate();
    const theme = useTheme();
    const [ sales, setSales ] = useState(false);
    const [ groupBy, setGroupBy ] = useState('created'); // 'created' or 'won'
    const [ wonDateMode, setWonDateMode ] = useState('last'); // 'first' or 'last'
    const resolveWonDate = (o) => wonDateMode === 'first' ? getFirstWonDate(o) : getWonDate(o);
    const [ drillDown, setDrillDown ] = useState(null); // { title, opportunities }

    const [ filter, setFilter ] = useState((() => {
        const filterStorage = sessionStorage.getItem('QuotationsAnalytics.filter');
        const base = { days: 'week', teamMember: me.email, accounts: [], priceQuote: 'all', rateRequest: 'all', taskStatus: 'all', taskType: 'all', tags: [], freetext: '' };
        try {
            return filterStorage ? JSON.parse(filterStorage) : base;
        } catch (e) {
            return base;
        }
    }));
    // Force all statuses and all types — analytics should always show full picture
    filter.taskStatus = 'all';
    filter.openTasksFilter = [];

    const filteredOpportunities = (opportunities || []).filter(r => r.status !== 'deleted');

    const [mainOpportunities, filteredAllTime, createdFiltered] = useMemo(() => {
        if (!filteredOpportunities || !opportunities || !org || !accounts) return [null, null, null];

        // Always compute the "By Created" set for funnel/sent counts in tables
        const _createdFiltered = applyFilter({ filter, opportunities: filteredOpportunities, org, accounts });

        if (groupBy === 'won') {
            // Get date range from filter
            const dateFrom = (() => {
                if (filter.days === 'week') return dayjs().startOf('isoWeek').valueOf();
                if (filter.days === 'month') return dayjs().startOf('month').valueOf();
                if (filter.days === 'quarter') return dayjs().startOf('quarter').valueOf();
                if (filter.days === 'past-week') return dayjs().subtract(1, 'week').startOf('isoWeek').valueOf();
                if (filter.days === 'past-month') return dayjs().subtract(1, 'month').startOf('month').valueOf();
                if (filter.days === 'past-quarter') return dayjs().subtract(1, 'quarter').startOf('quarter').valueOf();
                if (filter.days === 'custom' && filter.dateFrom) return dayjs(filter.dateFrom).startOf('day').valueOf();
                if (filter.days === 'all') return 0;
                return 0;
            })();
            const dateTo = (() => {
                if (filter.days === 'past-week') return dayjs().subtract(1, 'week').endOf('isoWeek').valueOf();
                if (filter.days === 'past-month') return dayjs().subtract(1, 'month').endOf('month').valueOf();
                if (filter.days === 'past-quarter') return dayjs().subtract(1, 'quarter').endOf('quarter').valueOf();
                if (filter.days === 'custom' && filter.dateTo) return dayjs(filter.dateTo).endOf('day').valueOf();
                return Date.now();
            })();

            // Filter by member/tags/etc but ignore date, then filter by won date
            const allFiltered = applyFilter({ filter: { ...filter, days: 'all' }, opportunities: filteredOpportunities, org, accounts });
            const wonFiltered = allFiltered.filter(o => {
                const wonDate = resolveWonDate(o);
                if (!wonDate) return false;
                return wonDate >= dateFrom && wonDate <= dateTo;
            });

            return [wonFiltered, allFiltered, _createdFiltered];
        }

        return [
            _createdFiltered,
            applyFilter({ filter: { ...filter, days: 'all' }, opportunities: filteredOpportunities, org, accounts }),
            _createdFiltered,
        ];
    }, [filter, filteredOpportunities, org, accounts, groupBy, wonDateMode]);

    if (!opportunities || !exchangeRates || !accounts) {
        return (
            <Box sx={{ padding: '36px 0px', background: '#fafbfc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={18} />
            </Box>
        );
    }

    const HOUR = 1 * 60 * 60 * 1000;
    const DAY = 24 * HOUR;

    const dateDays = (() => {
        if (filter.days === 'all') {
            if (_.isEmpty(mainOpportunities)) return 0;
            const spantime = _.maxBy(mainOpportunities, o => o.created)?.created - _.minBy(mainOpportunities, o => o.created)?.created;
            return Math.ceil(spantime / DAY);
        }
        if (filter.days === 'week') return dayjs().isoWeekday();
        if (filter.days === 'month') return dayjs().date();
        if (filter.days === 'quarter') return dayjs().diff(dayjs().startOf('quarter'), 'days');
        if (filter.days === 'past-week') return 7;
        if (filter.days === 'past-month') return dayjs().subtract(1, 'month').daysInMonth();
        if (filter.days === 'past-quarter') return dayjs().startOf('quarter').diff(dayjs().subtract(1, 'quarter').startOf('quarter'), 'days') - 1;
        if (filter.days !== 'custom') return filter.days;
        const dateFrom = new Date(filter.days === 'custom' ? (filter.dateFrom ? filter.dateFrom : new Date().getTime()) : new Date().getTime() - filter.days * DAY);
        dateFrom.setHours(0); dateFrom.setMinutes(0); dateFrom.setSeconds(0); dateFrom.setMilliseconds(0);
        const dateTo = new Date(filter.days === 'custom' && filter.dateTo ? filter.dateTo : new Date().getTime());
        dateTo.setHours(23); dateTo.setMinutes(59); dateTo.setSeconds(59); dateTo.setMilliseconds(999);
        return Math.floor((dateTo.getTime() - dateFrom.getTime()) / 1000 / 60 / 60 / 24);
    })();

    function setFilterWithStorage(newFilter) {
        if (typeof newFilter === 'function') {
            setFilter(prev => {
                const resolved = newFilter(prev);
                sessionStorage.setItem('QuotationsAnalytics.filter', JSON.stringify(resolved));
                return resolved;
            });
        } else {
            sessionStorage.setItem('QuotationsAnalytics.filter', JSON.stringify(newFilter));
            setFilter(newFilter);
        }
    }

    return (
        <Box sx={{ paddingBottom: '36px', background: '#fafbfc' }}>
            <Header
                icon={faChartMixed}
                title={'Quotations Analytics'}
            />
            <Box style={{ padding: '12px', paddingTop: '0px' }}>

                <OpportunitiesFilter
                    filter={filter}
                    setFilter={setFilterWithStorage}
                    me={me}
                    org={org}
                    opportunities={filteredOpportunities}
                    accounts={accounts}
                    groupedDays={true}
                    hideTaskStatus={true}
                />

            </Box>

            <Paper sx={{ padding: '24px', margin: '0px 12px', flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', paddingBottom: '16px', borderBottom: `1px solid ${grey[300]}` }}>
                    <Typography variant='body2' component={Box} sx={{ fontWeight: 800 }}>
                        Performance Overview
                    </Typography>
                    <ToggleButtonGroup
                        exclusive
                        size='small'
                        value={groupBy}
                        onChange={(e, val) => { if (val) setGroupBy(val); }}
                        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '11px', px: '12px', py: '3px' } }}
                    >
                        <Tooltip title="Show all opportunities created in the selected period, regardless of their current status" placement='bottom'>
                            <ToggleButton value='created'>By Created</ToggleButton>
                        </Tooltip>
                        <Tooltip title="Show only won and accepted opportunities, filtered by the date they were marked as Won or Accepted — not when they were created" placement='bottom'>
                            <ToggleButton value='won'>By Won</ToggleButton>
                        </Tooltip>
                    </ToggleButtonGroup>
                    {groupBy === 'won' && (
                        <ToggleButtonGroup
                            exclusive
                            size='small'
                            value={wonDateMode}
                            onChange={(e, val) => { if (val) setWonDateMode(val); }}
                            sx={{ ml: 1, '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '10px', px: '8px', py: '3px' } }}
                        >
                            <Tooltip title="Use the date the deal was first marked as Won — reflects when the sale was originally closed" placement='bottom'>
                                <ToggleButton value='first'>First Won</ToggleButton>
                            </Tooltip>
                            <Tooltip title="Use the most recent date the deal was marked as Won — reflects the latest status change" placement='bottom'>
                                <ToggleButton value='last'>Last Won</ToggleButton>
                            </Tooltip>
                        </ToggleButtonGroup>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                    {groupBy === 'created' && (
                        <>
                            <DraftsKPI opportunities={mainOpportunities} onClick={(opps) => setDrillDown({ title: 'Drafts', opportunities: opps })} />
                            <CreatedKPI opportunities={mainOpportunities} numDays={dateDays} exchangeRates={exchangeRates} org={org} onClick={(opps) => setDrillDown({ title: 'Quotes Sent', opportunities: opps })} />
                            <AcceptedKPI opportunities={mainOpportunities} org={org} onClick={(opps) => setDrillDown({ title: 'Quotes Won', opportunities: opps })} />
                            <SecuredKPI opportunities={mainOpportunities} exchangeRates={exchangeRates} org={org} onClick={(opps) => setDrillDown({ title: 'Revenue', opportunities: opps })} />
                            <GrossProfitKPI opportunities={mainOpportunities} exchangeRates={exchangeRates} org={org} onClick={(opps) => setDrillDown({ title: 'Gross Profit', opportunities: opps })} />
                        </>
                    )}
                    {groupBy === 'won' && (
                        <>
                            <AcceptedKPI opportunities={mainOpportunities} org={org} onClick={(opps) => setDrillDown({ title: 'Won Deals', opportunities: opps })} />
                            <SecuredKPI opportunities={mainOpportunities} exchangeRates={exchangeRates} org={org} onClick={(opps) => setDrillDown({ title: 'Revenue', opportunities: opps })} />
                            <WonCostsKPI opportunities={mainOpportunities} exchangeRates={exchangeRates} org={org} onClick={(opps) => setDrillDown({ title: 'Costs', opportunities: opps })} />
                            <GrossProfitKPI opportunities={mainOpportunities} exchangeRates={exchangeRates} org={org} onClick={(opps) => setDrillDown({ title: 'Gross Profit', opportunities: opps })} />
                            <AvgDealKPI opportunities={mainOpportunities} exchangeRates={exchangeRates} org={org} onClick={(opps) => setDrillDown({ title: 'All Won Deals', opportunities: opps })} />
                        </>
                    )}
                </Box>
            </Paper>
            <Paper sx={{ padding: '24px', margin: '12px', flex: 1 }}>
                <Box sx={{ marginBottom: '24px', paddingBottom: '16px', borderBottom: `1px solid ${grey[300]}` }}>
                    <Typography variant='body2' component={Box} sx={{ fontWeight: 800 }}>
                        Revenue & Profit Performance
                    </Typography>
                    <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                        {groupBy === 'won'
                            ? 'Grouped by the week each deal was won or accepted — shows how much revenue and profit was closed each week, regardless of when the opportunity was created.'
                            : 'Grouped by the week each deal was created — revenue and profit from those quotes that have since been won or accepted.'}
                    </Typography>
                </Box>
                <RevenueProfitChart
                    opportunities={filteredAllTime}
                    exchangeRates={exchangeRates}
                    organization={org}
                    wonDateMode={wonDateMode}
                    groupBy={groupBy}
                    type='weeks'
                    dateRange={(() => {
                        const from = (() => {
                            if (filter.days === 'week') return dayjs().startOf('isoWeek');
                            if (filter.days === 'month') return dayjs().startOf('month');
                            if (filter.days === 'quarter') return dayjs().startOf('quarter');
                            if (filter.days === 'past-week') return dayjs().subtract(1, 'week').startOf('isoWeek');
                            if (filter.days === 'past-month') return dayjs().subtract(1, 'month').startOf('month');
                            if (filter.days === 'past-quarter') return dayjs().subtract(1, 'quarter').startOf('quarter');
                            if (filter.days === 'custom' && filter.dateFrom) return dayjs(filter.dateFrom).startOf('day');
                            return null;
                        })();
                        const to = (() => {
                            if (filter.days === 'past-week') return dayjs().subtract(1, 'week').endOf('isoWeek');
                            if (filter.days === 'past-month') return dayjs().subtract(1, 'month').endOf('month');
                            if (filter.days === 'past-quarter') return dayjs().subtract(1, 'quarter').endOf('quarter');
                            if (filter.days === 'custom' && filter.dateTo) return dayjs(filter.dateTo).endOf('day');
                            return dayjs();
                        })();
                        return from ? { from: from.valueOf(), to: to.valueOf() } : null;
                    })()}
                />
            </Paper>
            <Paper sx={{ padding: '24px', margin: '12px', flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', paddingBottom: '16px', borderBottom: `1px solid ${grey[300]}` }}>
                    <Box>
                        <Typography variant='body2' component={Box} sx={{ fontWeight: 800 }}>
                            Team Performance
                        </Typography>
                        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                            {groupBy === 'won' ? 'Showing deals won or accepted in the selected period — revenue, profit, and funnel reflect only closed deals.' : 'Showing all quotes created in the selected period — full pipeline from draft to won/lost.'}
                        </Typography>
                    </Box>
                    <ToggleButtonGroup
                        exclusive
                        size='small'
                        value={groupBy}
                        onChange={(e, val) => { if (val) setGroupBy(val); }}
                        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '11px', px: '12px', py: '3px' } }}
                    >
                        <Tooltip title="Show all opportunities created in the selected period" placement='bottom'>
                            <ToggleButton value='created'>By Created</ToggleButton>
                        </Tooltip>
                        <Tooltip title="Revenue and profit from deals won in the selected period" placement='bottom'>
                            <ToggleButton value='won'>By Won</ToggleButton>
                        </Tooltip>
                    </ToggleButtonGroup>
                    {groupBy === 'won' && (
                        <ToggleButtonGroup exclusive size='small' value={wonDateMode} onChange={(e, val) => { if (val) setWonDateMode(val); }}
                            sx={{ ml: 1, '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '10px', px: '8px', py: '3px' } }}>
                            <Tooltip title="Use the date the deal was first marked as Won" placement='bottom'><ToggleButton value='first'>First Won</ToggleButton></Tooltip>
                            <Tooltip title="Use the most recent date the deal was marked as Won" placement='bottom'><ToggleButton value='last'>Last Won</ToggleButton></Tooltip>
                        </ToggleButtonGroup>
                    )}
                </Box>
                <TeamTable opportunities={mainOpportunities} allPeriodOpportunities={groupBy === 'won' ? mainOpportunities : createdFiltered} exchangeRates={exchangeRates} organization={org} onDrillDown={setDrillDown} groupBy={groupBy} />
            </Paper>
            <Paper sx={{ padding: '24px', margin: '12px', flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', paddingBottom: '16px', borderBottom: `1px solid ${grey[300]}` }}>
                    <Box>
                        <Typography variant='body2' component={Box} sx={{ fontWeight: 800 }}>
                            Accounts Overview
                        </Typography>
                        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                            {groupBy === 'won' ? 'Showing deals won or accepted in the selected period — revenue, profit, and funnel reflect only closed deals.' : 'Showing all quotes created in the selected period — full pipeline from draft to won/lost.'}
                        </Typography>
                    </Box>
                    <ToggleButtonGroup
                        exclusive
                        size='small'
                        value={groupBy}
                        onChange={(e, val) => { if (val) setGroupBy(val); }}
                        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '11px', px: '12px', py: '3px' } }}
                    >
                        <Tooltip title="Show all opportunities created in the selected period" placement='bottom'>
                            <ToggleButton value='created'>By Created</ToggleButton>
                        </Tooltip>
                        <Tooltip title="Revenue and profit from deals won in the selected period" placement='bottom'>
                            <ToggleButton value='won'>By Won</ToggleButton>
                        </Tooltip>
                    </ToggleButtonGroup>
                    {groupBy === 'won' && (
                        <ToggleButtonGroup exclusive size='small' value={wonDateMode} onChange={(e, val) => { if (val) setWonDateMode(val); }}
                            sx={{ ml: 1, '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '10px', px: '8px', py: '3px' } }}>
                            <Tooltip title="Use the date the deal was first marked as Won" placement='bottom'><ToggleButton value='first'>First Won</ToggleButton></Tooltip>
                            <Tooltip title="Use the most recent date the deal was marked as Won" placement='bottom'><ToggleButton value='last'>Last Won</ToggleButton></Tooltip>
                        </ToggleButtonGroup>
                    )}
                </Box>
                <AccountTable opportunities={mainOpportunities} allPeriodOpportunities={groupBy === 'won' ? mainOpportunities : createdFiltered} accounts={accounts} exchangeRates={exchangeRates} organization={org} onDrillDown={setDrillDown} groupBy={groupBy} />
            </Paper>
            <Box sx={{ margin: '12px', flex: 1, display: 'flex', gap: '24px' }}>
                <Paper sx={{ flex: 1, padding: '24px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                        <Typography variant='body2' component={Box} sx={{ fontWeight: 800, flex: 1, paddingBottom: '28px', borderBottom: `1px solid ${grey[300]}` }}>
                            Why Quotes Were Lost
                        </Typography>
                    </Box>
                    <LostReasonsPanel opportunities={createdFiltered} accounts={accounts} exchangeRates={exchangeRates} organization={org} onDrillDown={setDrillDown} />
                </Paper>
                <Paper sx={{ flex: 1, padding: '24px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                        <Typography variant='body2' component={Box} sx={{ fontWeight: 800, flex: 1, paddingBottom: '28px', borderBottom: `1px solid ${grey[300]}` }}>
                            Accounts
                        </Typography>
                    </Box>
                    <Typography variant='caption' sx={{ paddingBottom: '24px' }} component={Box}>
                        See how quotes break down between new customers and returning ones who requested rates before the selected time frame.
                    </Typography>
                    <NewVsExistingAccounts opportunities={mainOpportunities} allOpportunities={opportunities} />
                </Paper>
            </Box>
            <Paper sx={{ padding: '24px', margin: '12px', flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', paddingBottom: '16px', borderBottom: `1px solid ${grey[300]}` }}>
                    <Box>
                        <Typography variant='body2' component={Box} sx={{ fontWeight: 800 }}>
                            Sales/Quotes per Week
                        </Typography>
                        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                            {sales
                                ? 'Total sell value of quotes sent (blue) and won (green) per week, based on opportunity creation date.'
                                : 'Number of quotes sent (blue) and won (green) per week, based on opportunity creation date.'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: '4px' }}>
                        <Button color='primary' size='small' variant={sales ? 'contained' : 'outlined'} onClick={() => setSales(true)} sx={{ borderRadius: '4px', textTransform: 'none', fontSize: '11px' }}>
                            Sales
                        </Button>
                        <Button color='primary' size='small' variant={!sales ? 'contained' : 'outlined'} onClick={() => setSales(false)} sx={{ borderRadius: '4px', textTransform: 'none', fontSize: '11px' }}>
                            Quotes
                        </Button>
                    </Box>
                </Box>
                <SalesQuotesPer
                    opportunities={createdFiltered}
                    sales={sales}
                    exchangeRates={exchangeRates}
                    type='weeks'
                    organization={org}
                />
            </Paper>

            {/* Drill-down dialog */}
            {drillDown && (
                <DrillDownDialog
                    drillDown={drillDown}
                    onClose={() => setDrillDown(null)}
                    accounts={accounts}
                    exchangeRates={exchangeRates}
                    org={org}
                />
            )}
        </Box>
    )
}

function DraftsKPI(props) {
    const { opportunities, onClick } = props;
    const drafts = opportunities.filter(o => !o.status || o.status === 'draft' || o.status === 'pre-draft');
    return (
        <SingleKPI
            title='Drafts'
            value={drafts.length}
            caption={drafts.length === 0 ? 'No drafts in progress' : `${drafts.length} quote${drafts.length === 1 ? '' : 's'} not yet sent`}
            icon={faFileLines}
            color={grey}
            onClick={() => onClick && onClick(drafts)}
            info='Opportunities that have not been sent to the customer yet (draft or pre-draft status)'
        />
    );
}

function CreatedKPI(props) {
    const { opportunities, numDays, onClick, exchangeRates, org } = props;

    const fraction = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

    const sent = opportunities.filter(r => isSent(r));
    const lost = opportunities.filter(r => r.status === 'lost' || r.status === 'endcustomer-rejected');
    const pending = opportunities.filter(r => r.status === 'sent-to-endcustomer' || r.status === 'opened-by-customer');

    let pendingStr = '';
    if (exchangeRates && org && pending.length > 0) {
        const rc = calcRevenueAndCosts({ opportunities: pending, exchangeRates, organization: org });
        pendingStr = ` · Pending: ${currencyToText(org.defaults?.currency, true, fraction.format(rc.revenue).toLocaleString())}`;
    }

    return (
        <SingleKPI
            title='Quotes Sent'
            value={sent.length}
            caption={`${fraction.format(sent.length / (numDays || 1))} per day · ${lost.length} lost${pendingStr}`}
            icon={faEnvelope}
            color={purple}
            onClick={() => onClick && onClick(sent)}
            info='All opportunities that have been sent to a customer — includes Sent, Opened, Accepted, Won, Rejected, and Lost. Pending = revenue from quotes awaiting customer response.'
        />
    )
}

function AcceptedKPI(props) {
    const { opportunities, org, onClick } = props;

    const accepted = opportunities.filter(r => isAccepted(r));

    const fraction = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

    const sums = _(accepted)
        .map(opportunity => _(opportunity.products).map((p, i) => {
            const CW = calcChargeableWeight({
                product: p, transportationMethod: opportunity.transportationMethod, allProducts: opportunity.products, round: org.airRoundChargeableWeight });
            if (CW) return { type: 'chargeable Weight', value: CW }

            const WM = calcWeightOrMeasurment({ 
                product: p, transportationMethod: opportunity.transportationMethod, allProducts: opportunity.products, round: org.airRoundChargeableWeight });
            if (WM) return { type: 'weight or measurement', value: WM };

            // A product matching none of the cargo types above (e.g. an Air freight
            // product on a sea quote) has no container quantity — the unified
            // 'Air freight'/'LCL' types keep it inside calculations[]. Skip it rather
            // than counting it as a container with no value, which made sumBy return
            // undefined and blow up on toLocaleString.
            if (_.isUndefined(p.quantity)) return null;
            return { type: 'Containers', value: { val: p.quantity, postfix: '' } };
        }).compact().value()).
        flatten().
        groupBy(s => `${s.type}|${s.value.postfix}`).
        mapValues(v => _.sumBy(v, v2 => v2.value.val || 0)).
        value();

    const totals = _.map(sums, (v, k) => `Total ${k.split('|')[0]}: <b>${v.toLocaleString()} ${k.split('|')[1]}</b>`).join(' | ');
    const sent = opportunities.filter(r => isSent(r));

    return (
        <SingleKPI
            title='Won'
            value={sent.length > 0 ? `${accepted.length} (${fraction.format(accepted.length * 100 / sent.length)}%)` : '0'}
            caption={<span dangerouslySetInnerHTML={{ __html: totals }} />}
            icon={faTrophy}
            color={green}
            onClick={() => onClick && onClick(accepted)}
            info='Opportunities marked as Won by the team or Accepted by the customer via the offer link'
        />
    )
}

function SecuredKPI(props) {
    const { opportunities, exchangeRates, org, onClick } = props;

    const accepted = opportunities.filter(r => isAccepted(r));

    const fraction = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

    const rc = calcRevenueAndCosts({ opportunities: accepted, exchangeRates, organization: org });

    return (
        <SingleKPI
            title='Revenue'
            value={currencyToText(org.defaults?.currency, true, fraction.format(rc.revenue).toLocaleString())}
            caption={rc.revenue === 0 ? '' : `${fraction.format(rc.margin)}% margin · Cost: ${currencyToText(org.defaults?.currency, true, fraction.format(rc.costs).toLocaleString())}`}
            icon={faDollarSign}
            color={amber}
            onClick={() => onClick && onClick(accepted)}
            info='Total sell value from won and accepted opportunities, converted to your default currency'
        />
    )
}

function PendingKPI(props) {
    const { opportunities, exchangeRates, org, onClick } = props;

    const pending = opportunities.filter(r => 
        r.status === 'sent-to-endcustomer' ||
        r.status === 'opened-by-customer'
    );

    const fraction = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

    const rc = calcRevenueAndCosts({ opportunities: pending, exchangeRates, organization: org });

    const cargo = _(pending)
        .map(opportunity => _(opportunity.products).map((p, i) => {
            const CW = calcChargeableWeight({
                product: p, transportationMethod: opportunity.transportationMethod, allProducts: opportunity.products });
            if (CW) return { type: 'chargeable Weight', value: CW }

            const WM = calcWeightOrMeasurment({ 
                product: p, transportationMethod: opportunity.transportationMethod, allProducts: opportunity.products });
            if (WM) return { type: 'weight or measurement', value: WM };

            // A product matching none of the cargo types above (e.g. an Air freight
            // product on a sea quote) has no container quantity — the unified
            // 'Air freight'/'LCL' types keep it inside calculations[]. Skip it rather
            // than counting it as a container with no value, which made sumBy return
            // undefined and blow up on toLocaleString.
            if (_.isUndefined(p.quantity)) return null;
            return { type: 'Containers', value: { val: p.quantity, postfix: '' } };
        }).compact().value()).
        flatten().
        groupBy(s => `${s.type}|${s.value.postfix}`).
        mapValues(v => _.sumBy(v, v2 => v2.value.val || 0)).
        value();

    const cargoStr = _.map(cargo, (v, k) => `Total ${k.split('|')[0]}: <b>${v.toLocaleString()} ${k.split('|')[1]}</b>`).join(' | ');

    return (
        <SingleKPI
            title='Pending Revenue'
            value={currencyToText(org.defaults?.currency, true, fraction.format(rc.revenue).toLocaleString())}
            caption={<span>{pending.length} quote{pending.length === 1 ? '' : 's'} pending</span>}
            icon={faClock}
            color={red}
            onClick={() => onClick && onClick(pending)}
            info='Revenue from quotes sent or opened by the customer but not yet won, accepted, or lost'
        />
    )
}

function GrossProfitKPI(props) {
    const { opportunities, exchangeRates, org, onClick } = props;
    const accepted = opportunities.filter(r => isAccepted(r));
    const rc = calcRevenueAndCosts({ opportunities: accepted, exchangeRates, organization: org });

    return (
        <SingleKPI
            title='Gross Profit'
            value={currencyToText(org.defaults?.currency, true, rc.format.format(rc.gp).toLocaleString())}
            caption={rc.revenue > 0 ? `${rc.format.format(rc.margin)}% margin on ${accepted.length} won` : 'No won deals'}
            icon={faChartLineUp}
            color={rc.gp >= 0 ? green : red}
            onClick={() => onClick && onClick(accepted)}
            info='Revenue minus costs from won and accepted opportunities. Margin % = (Revenue - Costs) / Revenue'
        />
    );
}

function DrillDownDialog({ drillDown, onClose, accounts, exchangeRates, org }) {
    const [sortBy, setSortBy] = useState('created');
    const [sortDir, setSortDir] = useState('desc');
    const fraction = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

    const toggleSort = (col) => {
        if (sortBy === col) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(col);
            setSortDir('desc');
        }
    };

    const rows = drillDown.opportunities.map(o => {
        const rc = calcRevenueAndCosts({ opportunities: [o], exchangeRates, organization: org });
        const account = (accounts || []).find(a => a && a.id === o.account?.id);
        // A quote shows its figures once it has any price or cost, as in the opportunities table: one
        // priced at zero that still carries supplier costs has a real, negative GP, not a dash.
        const priced = rc.revenue !== 0 || rc.costs !== 0;
        return { o, rc, priced, account, choice: offerChoiceOf(o), firstWon: getFirstWonDate(o), lastWon: getWonDate(o) };
    });

    const sorted = _.orderBy(rows, [r => {
        if (sortBy === 'revenue') return r.rc.revenue;
        if (sortBy === 'gp') return r.rc.gp;
        if (sortBy === 'created') return r.o.created;
        if (sortBy === 'firstWon') return r.firstWon || 0;
        if (sortBy === 'lastWon') return r.lastWon || 0;
        return 0;
    }], [sortDir]);

    return (
        <Dialog open={true} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Typography variant='h6' sx={{ fontSize: '16px' }}>{drillDown.title} ({drillDown.opportunities.length})</Typography>
                <Button size='small' onClick={onClose}>Close</Button>
            </DialogTitle>
            <DialogContent>
                <Table size='small'>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Opportunity ID</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Mode</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Account</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Owner</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>
                                <TableSortLabel active={sortBy === 'created'} direction={sortBy === 'created' ? sortDir : 'desc'} onClick={() => toggleSort('created')}>
                                    Created
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>
                                <TableSortLabel active={sortBy === 'firstWon'} direction={sortBy === 'firstWon' ? sortDir : 'desc'} onClick={() => toggleSort('firstWon')}>
                                    First Won
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>
                                <TableSortLabel active={sortBy === 'lastWon'} direction={sortBy === 'lastWon' ? sortDir : 'desc'} onClick={() => toggleSort('lastWon')}>
                                    Last Won
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px' }} align='right'>
                                <TableSortLabel active={sortBy === 'revenue'} direction={sortBy === 'revenue' ? sortDir : 'desc'} onClick={() => toggleSort('revenue')}>
                                    Revenue
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '12px' }} align='right'>
                                <TableSortLabel active={sortBy === 'gp'} direction={sortBy === 'gp' ? sortDir : 'desc'} onClick={() => toggleSort('gp')}>
                                    Gross Profit
                                </TableSortLabel>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sorted.map(({ o, rc, priced, account, choice, firstWon, lastWon }) => (
                            <TableRow
                                key={o.id}
                                hover
                                sx={{ cursor: 'pointer', '& td': { fontSize: '12px' } }}
                                onClick={() => window.open(`/organization/${org.alias}/opportunities/${o.id}/`, '_blank')}
                            >
                                <TableCell sx={{ fontWeight: 600 }}>{readableId(o)}</TableCell>
                                <TableCell>{transportModeLabel(o)}</TableCell>
                                <TableCell>{account?.name || '—'}</TableCell>
                                <TableCell>{(o.owner || '').split('@')[0]}</TableCell>
                                <TableCell><Chip size='small' label={statusToText({ opportunity: o }) || 'Draft'} sx={{ fontSize: '10px', height: '20px' }} /></TableCell>
                                <TableCell>{dayjs(o.created).format('MMM D, YYYY')}</TableCell>
                                <TableCell>{firstWon ? dayjs(firstWon).format('MMM D, YYYY') : '—'}</TableCell>
                                <TableCell>{lastWon ? dayjs(lastWon).format('MMM D, YYYY') : '—'}</TableCell>
                                <TableCell align='right'>
                                    {priced ? currencyToText(org.defaults?.currency, true, fraction.format(rc.revenue)) : '—'}
                                    {/* A quote with several offers is counted at one of them: say which, and when
                                        nobody said which, that it is the first by default. */}
                                    {choice.count > 1 && (
                                        <Typography component='div' sx={{ fontSize: '10px', color: choice.chosen ? 'text.secondary' : 'warning.dark' }}>
                                            Offer {choice.index + 1} of {choice.count}{choice.chosen ? '' : ' · none chosen'}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell align='right' sx={{ color: rc.gp >= 0 ? green[700] : red[500], fontWeight: 600 }}>{priced ? currencyToText(org.defaults?.currency, true, fraction.format(rc.gp)) : '—'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </DialogContent>
        </Dialog>
    );
}

function WonCostsKPI(props) {
    const { opportunities, exchangeRates, org, onClick } = props;
    const accepted = opportunities.filter(r => isAccepted(r));
    const rc = calcRevenueAndCosts({ opportunities: accepted, exchangeRates, organization: org });

    return (
        <SingleKPI
            title='Costs'
            value={currencyToText(org.defaults?.currency, true, rc.format.format(rc.costs).toLocaleString())}
            caption={accepted.length > 0 ? `Avg ${currencyToText(org.defaults?.currency, true, rc.format.format(rc.costs / accepted.length).toLocaleString())} per deal` : 'No won deals'}
            icon={faDollarSign}
            color={red}
            onClick={() => onClick && onClick(accepted)}
            info='Total supplier costs from won and accepted opportunities'
        />
    );
}

function AvgDealKPI(props) {
    const { opportunities, exchangeRates, org, onClick } = props;
    const accepted = opportunities.filter(r => isAccepted(r));
    const rc = calcRevenueAndCosts({ opportunities: accepted, exchangeRates, organization: org });
    const avg = accepted.length > 0 ? rc.revenue / accepted.length : 0;

    return (
        <SingleKPI
            title='Avg Deal Size'
            value={currencyToText(org.defaults?.currency, true, rc.format.format(avg).toLocaleString())}
            caption={accepted.length > 0 ? `Across ${accepted.length} won deal${accepted.length === 1 ? '' : 's'}` : 'No won deals'}
            icon={faChartLineUp}
            color={cyan}
            onClick={() => onClick && onClick(accepted)}
            info='Average revenue per won or accepted opportunity'
        />
    );
}

function SingleKPI(props) {
    const { title, value, caption, icon, color, onHoverText, onClick, info } = props;
    const [ hover, setHover ] = useState(false);
    return (
        <Box 
            sx={{ 
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                padding: '16px 20px',
                border: `1px solid ${grey[300]}`,
                borderRadius: '8px',
                background: 'white',
                flex: 1,
                minWidth: 0,
                ...(hover && { border: `1px solid ${blue[500]}` }),
                ...(onClick && { cursor: 'pointer' })
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onClick={onClick}
        >
            <Box sx={{ display: 'flex', marginBottom: '6px', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Typography variant='body2' component={Box} sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {title}
                        {info && (
                            <Tooltip title={info} placement='top' arrow>
                                <Box component='span' sx={{ cursor: 'help', opacity: 0.4, fontSize: '11px', '&:hover': { opacity: 0.8 } }}>
                                    <FontAwesomeIcon icon={faCircleInfo} />
                                </Box>
                            </Tooltip>
                        )}
                    </Typography>
                    <Typography variant='h6' component={Box} sx={{ fontWeight: 800, flex: 1 }}>
                        {value}
                    </Typography>
                </Box>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: color[100],
                    color: color[500],
                    width: '50px',
                    height: '50px',
                    borderRadius: '12px',
                    marginLeft: '12px',
                    fontSize: '1.2em'
                }}>
                    <FontAwesomeIcon icon={icon} />
                </Box>
            </Box>
            <Typography variant='caption' component={Box} sx={{ color: grey[500], minHeight: '32px', fontSize: '11px', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                {caption}
            </Typography>
            {hover && onHoverText && (
                <Box sx={{ position: 'absolute', top: '0px', bottom: '0px', left: '0px', right: '0px', background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant='caption' component={Box} sx={{ color: 'white', fontWeight: 800 }}>
                        {onHoverText}
                    </Typography>
                </Box>
            )}
        </Box>
    )
}

function TeamTable(props) {
    const { opportunities, allPeriodOpportunities, exchangeRates, organization, onDrillDown, groupBy } = props;
    const funnelOpps = allPeriodOpportunities || opportunities;
    const isWon = groupBy === 'won';

    const owners = _.uniq([
        ...(organization?.users || []).map(u => u.email),
        ...funnelOpps.map(o => o.owner),
        ...opportunities.map(o => o.owner),
    ].filter(Boolean));

    const rows = owners
        .map(owner => {
            const row = renderTeamRow({ owner, opportunities, funnelOpportunities: funnelOpps, exchangeRates, organization, isWon });
            if (row && onDrillDown) {
                const ownerOpps = funnelOpps.filter(o => o.owner === owner);
                row.onClick = () => onDrillDown({ title: (owner || '').split('@')[0], opportunities: ownerOpps });
            }
            return row;
        })
        .filter(Boolean);

    const headers = isWon ? [
        { key: 'owner', text: 'Team Member', info: 'The opportunity owner (assigned team member)' },
        { key: 'won', text: 'Won', info: 'Number of deals won or accepted in this period' },
        { key: 'cargo', text: 'Cargo Secured', info: 'Total cargo volume from won deals (TEUs for FCL: 20ft=1 TEU, 40ft=2 TEUs, or weight/volume for LCL/Air)' },
        { key: 'accounts', text: 'Accounts', info: 'Number of unique customer accounts with won deals' },
        { key: 'revenue', text: 'Revenue', info: 'Total sell value from won and accepted opportunities' },
        { key: 'gp', text: 'Profit', info: 'Revenue minus Costs. Profit includes buy/sell rate differential.' },
        { key: 'margin', text: 'Margin', info: 'Gross Profit as a percentage of Revenue' },
    ] : [
        { key: 'owner', text: 'Team Member', info: 'The opportunity owner (assigned team member)' },
        { key: 'quotes', text: 'Funnel (S/W/R)', info: '(S/W/R) = Sent / Won / Rejected. Shows the full quote funnel for each team member.' },
        { key: 'win-rate', text: 'Win Rate', info: 'Percentage of sent quotes that were won or accepted (Won + Accepted / Sent)' },
        { key: 'revenue', text: 'Revenue', info: 'Total sell value from won and accepted opportunities' },
        { key: 'gp', text: 'Profit', info: 'Revenue minus Costs. Profit includes buy/sell rate differential.' },
        { key: 'margin', text: 'Margin', info: 'Gross Profit as a percentage of Revenue' },
    ];

    return (
        <SortablePaginationTable headers={headers} rows={rows} />
    );
}

function AccountTable(props) {
    const { opportunities, allPeriodOpportunities, accounts, exchangeRates, organization, onDrillDown, groupBy } = props;
    const funnelOpps = allPeriodOpportunities || opportunities;
    const isWon = groupBy === 'won';

    const accountIds = _.uniq([
        ...funnelOpps.filter(o => o.account?.id).map(o => o.account.id),
        ...opportunities.filter(o => o.account?.id).map(o => o.account.id),
    ]);

    const rows = accountIds
        .map(id => (accounts || []).find(ac => ac && ac.id === id))
        .filter(account => account !== undefined && account !== null)
        .map(account => {
            const row = renderAccountRow({ account, opportunities, funnelOpportunities: funnelOpps, exchangeRates, organization, isWon });
            if (row && onDrillDown) {
                const accountOpps = funnelOpps.filter(o => o.account?.id === account.id);
                row.onClick = () => onDrillDown({ title: account.name, opportunities: accountOpps });
            }
            return row;
        })
        .filter(Boolean);

    const headers = isWon ? [
        { key: 'account', text: 'Account Name', info: 'The customer account linked to the opportunity' },
        { key: 'won', text: 'Won', info: 'Number of deals won or accepted for this account' },
        { key: 'cargo', text: 'Cargo Secured', info: 'Total cargo volume from won deals (TEUs for FCL, or weight/volume for LCL/Air)' },
        { key: 'revenue', text: 'Revenue', info: 'Total sell value from won and accepted opportunities' },
        { key: 'gp', text: 'Profit', info: 'Revenue minus Costs. Profit includes buy/sell rate differential.' },
        { key: 'margin', text: 'Margin', info: 'Gross Profit as a percentage of Revenue' },
    ] : [
        { key: 'account', text: 'Account Name', info: 'The customer account linked to the opportunity' },
        { key: 'quotes', text: 'Funnel (S/W/R)', info: '(S/W/R) = Sent / Won / Rejected. Shows the full quote funnel for each account.' },
        { key: 'win-rate', text: 'Win Rate', info: 'Percentage of sent quotes that were won or accepted for this account' },
        { key: 'revenue', text: 'Revenue', info: 'Total sell value from won and accepted opportunities' },
        { key: 'gp', text: 'Profit', info: 'Revenue minus Costs. Profit includes buy/sell rate differential.' },
        { key: 'margin', text: 'Margin', info: 'Gross Profit as a percentage of Revenue' },
    ];

    return (
        <SortablePaginationTable headers={headers} rows={rows} />
    );
}

function SortablePaginationTable(props) {
    const { headers, rows } = props;

    const [ sortBy, setSortBy ] = useState(headers[0].key);
    const [ sortDirection, setSortDirection ] = useState('asc');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
  
    const onChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const onChangePage = (event, newPage) => {
        setPage(newPage);
    };

    function _setSortBy(newSortBy) {
        if (newSortBy === sortBy) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortDirection('asc');
            setSortBy(newSortBy);
        }
    }

    let sortedRows = _(rows)
        .compact()
        .sortBy(r => r.cells.find(c => c.key === sortBy)?.value ?? 0)
        .value();
    if (sortDirection === 'desc') sortedRows = sortedRows.reverse();

    const window = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            {headers.map(h => (
                                <TableCell key={h.key}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <TableSortLabel
                                            active={sortBy === h.key}
                                            direction={sortDirection}
                                            onClick={() => _setSortBy(h.key)}
                                        >
                                            {h.text}
                                        </TableSortLabel>
                                        {h.info && (
                                            <Tooltip title={h.info} placement='top' arrow>
                                                <Box component='span' sx={{ cursor: 'help', opacity: 0.3, fontSize: '11px', '&:hover': { opacity: 0.7 } }}>
                                                    <FontAwesomeIcon icon={faCircleInfo} />
                                                </Box>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {window.map(r => (r ? (
                            <TableRow key={r.key} hover={!!r.onClick} sx={r.onClick ? { cursor: 'pointer' } : {}} onClick={r.onClick}>
                                {r.cells.map(c => <TableCell key={c.key}>{c.text}</TableCell>)}
                            </TableRow>
                        ) : null))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component='div'
                count={sortedRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onChangePage}
                onRowsPerPageChange={onChangeRowsPerPage}
            />
        </>
    );
}

function calcCargoSummary(opportunities, organization) {
    const fmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
    let teus = 0;
    const otherSums = {};

    for (const o of opportunities) {
        for (const p of o.products || []) {
            const qty = p.quantity || 1;
            const pType = (p.type || '').toLowerCase();

            // FCL containers — calculate TEUs
            if (pType.includes('40')) {
                teus += 2 * qty;
                continue;
            }
            if (pType.includes('20')) {
                teus += 1 * qty;
                continue;
            }

            // LCL / Air — use weight or measurement
            const CW = calcChargeableWeight({ product: p, transportationMethod: o.transportationMethod, allProducts: o.products, round: organization?.airRoundChargeableWeight });
            if (CW) {
                const key = `CW|${CW.postfix}`;
                otherSums[key] = (otherSums[key] || 0) + CW.val;
                continue;
            }
            const WM = calcWeightOrMeasurment({ product: p, transportationMethod: o.transportationMethod, allProducts: o.products });
            if (WM) {
                const key = `WM|${WM.postfix}`;
                otherSums[key] = (otherSums[key] || 0) + WM.val;
                continue;
            }
        }
    }

    const parts = [];
    if (teus > 0) parts.push(`${fmt.format(teus)} TEUs`);
    for (const [k, v] of Object.entries(otherSums)) {
        const postfix = k.split('|')[1];
        parts.push(`${fmt.format(v)} ${postfix}`);
    }
    return parts.join(' · ') || '—';
}

function QuotesBadge({ created, accepted, rejected }) {
    const badgeSx = (color, bg) => ({
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: '28px', px: '6px', py: '2px', borderRadius: '6px',
        fontSize: '12px', fontWeight: 600, color, backgroundColor: bg,
    });
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Box sx={badgeSx(grey[700], grey[100])}>{created}</Box>
            <Typography sx={{ color: grey[400], fontSize: '12px' }}>/</Typography>
            <Box sx={badgeSx(blue[700], blue[50])}>{accepted}</Box>
            <Typography sx={{ color: grey[400], fontSize: '12px' }}>/</Typography>
            <Box sx={badgeSx(red[600], red[50])}>{rejected}</Box>
        </Box>
    );
}

function WinRateDot({ rate }) {
    const color = rate >= 60 ? green[500] : rate >= 40 ? amber[500] : red[400];
    const size = Math.max(6, Math.min(12, rate / 10));
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Box sx={{ width: size, height: size, borderRadius: '50%', backgroundColor: color }} />
            <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{rate.toFixed(0)}%</Typography>
        </Box>
    );
}

function renderTeamRow(props) {
    const { owner, opportunities, funnelOpportunities, exchangeRates, organization, isWon } = props;

    const fraction = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    const funnelOwner = (funnelOpportunities || opportunities).filter(o => o.owner === owner);
    const ownerAccepted = opportunities.filter(o => o.owner === owner && isAccepted(o));
    const rc = calcRevenueAndCosts({ opportunities: ownerAccepted, exchangeRates, organization });

    if (isWon) {
        const cargo = ownerAccepted.length > 0 ? calcCargoSummary(ownerAccepted, organization) : '';
        const uniqueAccounts = _.uniq(ownerAccepted.filter(o => o.account?.id).map(o => o.account.id)).length;

        return {
            key: owner,
            cells: [
                { key: 'owner', value: (owner || '').split('@')[0], text: (owner || '').split('@')[0] },
                { key: 'won', value: ownerAccepted.length, text: ownerAccepted.length },
                { key: 'cargo', value: 0, text: <Typography sx={{ fontSize: '12px' }}>{cargo}</Typography> },
                { key: 'accounts', value: uniqueAccounts, text: uniqueAccounts },
                { key: 'revenue', value: rc.revenue, text: currencyToText(organization.defaults?.currency, true, fraction.format(rc.revenue).toLocaleString()) },
                { key: 'gp', value: rc.gp, text: <Typography sx={{ fontWeight: 700, color: rc.gp >= 0 ? green[700] : red[500], fontSize: '13px' }}>{currencyToText(organization.defaults?.currency, true, fraction.format(rc.gp).toLocaleString())}</Typography> },
                { key: 'margin', value: rc.margin, text: <Typography sx={{ fontWeight: 600, fontSize: '13px', color: rc.margin >= 15 ? green[700] : rc.margin >= 10 ? amber[800] : red[500] }}>{fraction.format(rc.margin).toLocaleString()}%</Typography> },
            ],
        };
    }

    const funnelSent = funnelOwner.filter(r => isSent(r));
    const funnelAccepted = funnelOwner.filter(r => isAccepted(r));
    const funnelRejected = funnelOwner.filter(r => r.status === 'lost' || r.status === 'endcustomer-rejected');
    const winRate = funnelSent.length > 0 ? (funnelAccepted.length / funnelSent.length) * 100 : 0;

    return {
        key: owner,
        cells: [
            { key: 'owner', value: (owner || '').split('@')[0], text: (owner || '').split('@')[0] },
            { key: 'quotes', value: funnelSent.length, text: <QuotesBadge created={funnelSent.length} accepted={funnelAccepted.length} rejected={funnelRejected.length} /> },
            { key: 'win-rate', value: winRate, text: <WinRateDot rate={winRate} /> },
            { key: 'revenue', value: rc.revenue, text: currencyToText(organization.defaults?.currency, true, fraction.format(rc.revenue).toLocaleString()) },
            { key: 'gp', value: rc.gp, text: <Typography sx={{ fontWeight: 700, color: rc.gp >= 0 ? green[700] : red[500], fontSize: '13px' }}>{currencyToText(organization.defaults?.currency, true, fraction.format(rc.gp).toLocaleString())}</Typography> },
            { key: 'margin', value: rc.margin, text: <Typography sx={{ fontWeight: 600, fontSize: '13px', color: rc.margin >= 15 ? green[700] : rc.margin >= 10 ? amber[800] : red[500] }}>{fraction.format(rc.margin).toLocaleString()}%</Typography> },
        ],
    };
}

function renderAccountRow(props) {
    const { account, opportunities, funnelOpportunities, exchangeRates, organization, isWon } = props;

    if (!account || !account.id) return null;

    const fraction = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    const accountAccepted = opportunities.filter(o => o.account && o.account.id === account.id && isAccepted(o));
    const rc = calcRevenueAndCosts({ opportunities: accountAccepted, exchangeRates, organization });

    if (isWon) {
        if (accountAccepted.length === 0) return null;
        const cargo = calcCargoSummary(accountAccepted, organization);

        return {
            key: account.id,
            cells: [
                { key: 'account', value: (account.name || '').toLowerCase().trim(), text: account.name },
                { key: 'won', value: accountAccepted.length, text: accountAccepted.length },
                { key: 'cargo', value: 0, text: <Typography sx={{ fontSize: '12px' }}>{cargo}</Typography> },
                { key: 'revenue', value: rc.revenue, text: currencyToText(organization.defaults?.currency, true, fraction.format(rc.revenue).toLocaleString()) },
                { key: 'gp', value: rc.gp, text: <Typography sx={{ fontWeight: 700, color: rc.gp >= 0 ? green[700] : red[500], fontSize: '13px' }}>{currencyToText(organization.defaults?.currency, true, fraction.format(rc.gp).toLocaleString())}</Typography> },
                { key: 'margin', value: rc.margin, text: <Typography sx={{ fontWeight: 600, fontSize: '13px', color: rc.margin >= 15 ? green[700] : rc.margin >= 10 ? amber[800] : red[500] }}>{fraction.format(rc.margin).toLocaleString()}%</Typography> },
            ],
        };
    }

    const funnelAccount = (funnelOpportunities || opportunities).filter(o => o.account && o.account.id === account.id);
    const funnelSent = funnelAccount.filter(r => isSent(r));
    const funnelAccepted = funnelAccount.filter(r => isAccepted(r));
    const funnelRejected = funnelAccount.filter(r => r.status === 'lost' || r.status === 'endcustomer-rejected');
    if (funnelSent.length === 0) return null;
    const winRate = funnelSent.length > 0 ? (funnelAccepted.length / funnelSent.length) * 100 : 0;

    return {
        key: account.id,
        cells: [
            { key: 'account', value: (account.name || '').toLowerCase().trim(), text: account.name },
            { key: 'quotes', value: funnelSent.length, text: <QuotesBadge created={funnelSent.length} accepted={funnelAccepted.length} rejected={funnelRejected.length} /> },
            { key: 'win-rate', value: winRate, text: <WinRateDot rate={winRate} /> },
            { key: 'revenue', value: rc.revenue, text: currencyToText(organization.defaults?.currency, true, fraction.format(rc.revenue).toLocaleString()) },
            { key: 'gp', value: rc.gp, text: <Typography sx={{ fontWeight: 700, color: rc.gp >= 0 ? green[700] : red[500], fontSize: '13px' }}>{currencyToText(organization.defaults?.currency, true, fraction.format(rc.gp).toLocaleString())}</Typography> },
            { key: 'margin', value: rc.margin, text: <Typography sx={{ fontWeight: 600, fontSize: '13px', color: rc.margin >= 15 ? green[700] : rc.margin >= 10 ? amber[800] : red[500] }}>{fraction.format(rc.margin).toLocaleString()}%</Typography> },
        ],
    };
}

// Lost and declined quotes grouped by why they were lost. The reason comes from the "Mark as Lost"
// dialog (opportunity.lostReason); quotes the customer declined without one being picked are
// grouped as declined, and older quotes from before the dialog existed as "No reason given".
const DECLINED_BUCKET = 'declined';
const NO_REASON_BUCKET = 'none';
const LOST_BUCKETS = [
    ...LOST_REASONS.map(r => ({ key: r.key, label: r.label })),
    { key: DECLINED_BUCKET, label: 'Declined by the customer' },
    { key: NO_REASON_BUCKET, label: 'No reason given' },
];

function lostBucket(opportunity) {
    if (opportunity.lostReason?.category) return opportunity.lostReason.category;
    if (opportunity.status === 'endcustomer-rejected' || (opportunity.log || []).some(l => l.action === 'rejected')) return DECLINED_BUCKET;
    return NO_REASON_BUCKET;
}

function LostReasonsPanel(props) {
    const { opportunities, accounts, exchangeRates, organization, onDrillDown } = props;
    const theme = useTheme();
    const accountName = o => (accounts || []).find(a => a && a.id === o.account?.id)?.name;

    const lost = opportunities.filter(o => o.status === 'endcustomer-rejected' || o.status === 'lost');

    if (lost.length === 0) {
        return <Typography variant='body2' sx={{ color: 'text.secondary' }}>No lost quotes in this period.</Typography>;
    }

    const groups = _.groupBy(lost, lostBucket);
    const withReason = lost.filter(o => o.lostReason?.category).length;
    const fraction = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    const currency = organization?.defaults?.currency;

    const rows = LOST_BUCKETS
        .filter(b => (groups[b.key] || []).length > 0)
        .map(b => ({
            ...b,
            opportunities: groups[b.key],
            value: calcRevenueAndCosts({ opportunities: groups[b.key], exchangeRates, organization }).revenue,
        }))
        .sort((a, b) => b.opportunities.length - a.opportunities.length);

    // The words behind the numbers: "Other" explanations and what customers wrote when declining.
    const notes = _.orderBy(lost.map(o => {
        if (o.lostReason?.category === 'other' && o.lostReason.text) {
            return { o, text: o.lostReason.text, by: o.lostReason.by, at: o.lostReason.at };
        }
        const rejection = _.findLast(o.log || [], l => l.action === 'rejected' && l.reason);
        if (rejection) return { o, text: rejection.reason, by: rejection.customerName || rejection.modifiedBy || 'Customer', at: rejection.timestamp };
        return null;
    }).filter(Boolean), n => n.at || 0, 'desc').slice(0, 5);

    return (
        <Box>
            <Typography variant='caption' component={Box} sx={{ color: 'text.secondary', marginBottom: '16px' }}>
                Of the quotes created in this period, {lost.length} {lost.length === 1 ? 'was' : 'were'} lost and {withReason} of those have a reason picked. Click a reason to see its quotes.
            </Typography>
            {rows.map(row => {
                const count = row.opportunities.length;
                const percentage = (count / lost.length) * 100;
                return (
                    <Box
                        key={row.key}
                        onClick={() => onDrillDown({ title: `Lost: ${row.label}`, opportunities: row.opportunities })}
                        sx={{ mb: 2, cursor: 'pointer', borderRadius: '6px', padding: '4px 6px', margin: '0 -6px 12px', '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.05) } }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, gap: '12px' }}>
                            <Typography variant="body2">{row.label}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                {count} ({percentage.toFixed(1)}%){row.value > 0 ? ` · ${currencyToText(currency, true, fraction.format(row.value))}` : ''}
                            </Typography>
                        </Box>
                        <Box sx={{ width: '100%', bgcolor: grey[100], borderRadius: 1, height: 8 }}>
                            <Box
                                sx={{
                                    width: `${percentage}%`,
                                    bgcolor: row.key === NO_REASON_BUCKET ? grey[400] : '#4d4ab6',
                                    height: '100%',
                                    borderRadius: 1,
                                    transition: 'width 0.5s ease-in-out',
                                }}
                            />
                        </Box>
                    </Box>
                );
            })}
            {notes.length > 0 && (
                <Box sx={{ marginTop: '20px', paddingTop: '12px', borderTop: `1px solid ${grey[200]}` }}>
                    <Typography variant='caption' component={Box} sx={{ fontWeight: 700, marginBottom: '8px' }}>Latest notes</Typography>
                    {notes.map(n => (
                        <Box key={`${n.o.id}-${n.at}`} sx={{ marginBottom: '8px' }}>
                            <Typography variant='body2' sx={{ fontSize: '12px' }}>"{n.text}"</Typography>
                            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                                {accountName(n.o) || readableId(n.o)}{n.by ? ` · ${n.by.split('@')[0]}` : ''}{n.at ? ` · ${dayjs(n.at).format('MMM D, YYYY')}` : ''}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}

function NewVsExistingAccounts(props) {
    const { opportunities, allOpportunities } = props;

    const earliest = _(opportunities).minBy(o => o.created)?.created;
    const pastOpportunities = allOpportunities.filter(o => o.created < earliest);

    const accounts = _(opportunities)
        .filter(o => o.account && o.account.id) // Filter out opportunities without accounts
        .map(o => o.account.id)
        .uniq()
        .value();

    const pastAccounts = _(pastOpportunities)
        .filter(o => o.account && o.account.id) // Filter out opportunities without accounts
        .map(o => o.account.id)
        .uniq()
        .value();

    const entries = {
        'New Customers': accounts.filter(a => !pastAccounts.includes(a)).length,
        Repeated: accounts.filter(a => pastAccounts.includes(a)).length,
    };

    const colors = [ '#4d4ab6', grey[400] ];

    return (
        <Box>
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Box sx={{ position: 'relative', width: 200, height: 200 }}>
                    {Object.entries(entries).map(([type, count], index) => {
                        const percentage = (count / accounts.length) * 100;
                        const previousSegments = Object.entries(entries)
                            .slice(0, index)
                            .reduce((acc, [_, segmentCount]) =>
                                acc + (segmentCount / accounts.length) * 100, 0);

                        return (
                            <Box
                                key={type}
                                sx={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    background: `conic-gradient(transparent ${previousSegments}%, ${colors[index]} ${previousSegments}%, ${colors[index]} ${previousSegments + percentage}%, transparent ${previousSegments + percentage}%)`,
                                    borderRadius: '50%',
                                }}
                            />
                        );
                    })}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {Object.entries(entries).map(([type, count], index) => (
                    <Box key={type} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }} key={type}>
                        <Typography variant='h6'>
                            {count}
                        </Typography>
                        <Typography variant='body2' component={Box} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ background: colors[index], display: 'inline-block', width: 10, height: 10, borderRadius: '50%', mr: 1 }} />
                            <Box>
                                {type}
                            </Box>
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

function SalesQuotesPer(props) {
    const { opportunities, sales, exchangeRates, type, organization } = props;

    const fraction = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

    const sections = _(opportunities)
        .groupBy(o => 
            type === 'months' ?  new Date(o.created).getFullYear() * 12 + new Date(o.created).getMonth() :
            type === 'weeks' ? dayjs(o.created).startOf('isoWeek').unix() :
            type === 'quarters' ? dayjs(o.created).startOf('quarter').unix() :
            null
        ).value();

    const keys = _.keys(sections).map(k => parseInt(k)).sort();
    let min = keys[0];
    const max = keys[keys.length - 1];

    while (min < max) {
        keys.push(min);
        if (type === 'months') min++; 
        else if (type === 'weeks') min = dayjs.unix(min).add(1, 'week').unix();
        else if (type === 'quarters') min = dayjs.unix(min).add(1, 'quarter').unix();
    }

    const data = _(keys).uniq().sort().map(key => {
        const section = sections[key] || [];

        const sent = _.sum(section.filter(o => isSent(o)).map(o => {
            if (!sales) return 1;
            return calcRevenueAndCosts({ opportunities: [o], exchangeRates, organization }).revenue;
        }));

        const accepted = _.sum(section.filter(o => isAccepted(o)).map(o => {
            if (!sales) return 1;
            return calcRevenueAndCosts({ opportunities: [o], exchangeRates, organization }).revenue;
        }));

        return {
            xAxis: type === 'months' ? dayjs(new Date(Math.floor(key / 12), key % 12)).format('MMMM YYYY') :
                type === 'weeks' ? dayjs.unix(key).format('MMMM Do YYYY') :
                type === 'quarters' ? `Q${Math.floor((dayjs.unix(key).month() / 3)) + 1} ${dayjs.unix(key).format('YYYY')}`:
                null,
            sent,
            accepted
        };
    }).value();

    return (
        <LineChart
            height={500}
            series={[
                {
                    data: data.map(d => d.sent),
                    label: 'Quotes Sent',
                    color: '#605dec',
                    valueFormatter: (value) => sales ? currencyToText(organization.defaults?.currency, true, fraction.format(value).toLocaleString()) : value,
                },
                {
                    data: data.map(d => d.accepted),
                    label: 'Quotes Accepted',
                    color: '#4CAF50', 
                    valueFormatter: (value) => sales ? currencyToText(organization.defaults?.currency, true, fraction.format(value).toLocaleString()) : value,
                }
            ]}
            xAxis={[{
                data: data.map(d => d.xAxis),
                scaleType: 'point'
            }]}
            yAxis={[{
                min: 0,
                tickInterval: 1
            }]}
            sx={{
                '.MuiLineElement-root': {
                    strokeWidth: 2
                }
            }}
        />
    );
}

function RevenueProfitChart(props) {
    const { opportunities, exchangeRates, organization, type, dateRange, wonDateMode = 'last', groupBy = 'won' } = props;
    // 'won' mode buckets by the won/accepted date; 'created' mode buckets by the created
    // date so the chart mirrors the By-Created overview (won deals, grouped by created week).
    const _getWonDate = (o) => groupBy === 'created' ? o.created : (wonDateMode === 'first' ? getFirstWonDate(o) : getWonDate(o));
    const cur = organization.defaults?.currency || 'USD';
    const fraction = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

    const sections = _(opportunities)
        .filter(o => isAccepted(o))
        .filter(o => _getWonDate(o))
        .filter(o => {
            if (!dateRange) return true;
            const wonDate = _getWonDate(o);
            return wonDate >= dateRange.from && wonDate <= dateRange.to;
        })
        .groupBy(o => {
            const wonDate = _getWonDate(o);
            return type === 'months' ? new Date(wonDate).getFullYear() * 12 + new Date(wonDate).getMonth() :
                type === 'weeks' ? dayjs(wonDate).startOf('isoWeek').unix() :
                type === 'quarters' ? dayjs(wonDate).startOf('quarter').unix() :
                null;
        }).value();

    const keys = _.keys(sections).map(k => parseInt(k)).sort();
    let min = keys[0];
    const max = keys[keys.length - 1];

    while (min < max) {
        keys.push(min);
        if (type === 'months') min++;
        else if (type === 'weeks') min = dayjs.unix(min).add(1, 'week').unix();
        else if (type === 'quarters') min = dayjs.unix(min).add(1, 'quarter').unix();
    }

    const data = _(keys).uniq().sort().map(key => {
        const section = sections[key] || [];
        const rc = calcRevenueAndCosts({ opportunities: section, exchangeRates, organization });

        return {
            xAxis: type === 'months' ? dayjs(new Date(Math.floor(key / 12), key % 12)).format('MMM YYYY') :
                type === 'weeks' ? `W${dayjs.unix(key).isoWeek()} (${dayjs.unix(key).format('MMM D')}–${dayjs.unix(key).endOf('isoWeek').format('D')})` :
                type === 'quarters' ? `Q${Math.floor((dayjs.unix(key).month() / 3)) + 1} ${dayjs.unix(key).format('YYYY')}` :
                null,
            revenue: rc.revenue,
            profit: rc.gp,
        };
    }).value();

    if (data.length === 0) {
        return <Typography variant='body2' sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No won opportunities to display</Typography>;
    }

    return (
        <BarChart
            height={400}
            series={[
                {
                    data: data.map(d => d.revenue),
                    label: 'Revenue',
                    color: '#4F7DF3',
                    valueFormatter: (value) => currencyToText(cur, true, fraction.format(value)),
                },
                {
                    data: data.map(d => d.profit),
                    label: 'Profit',
                    color: '#F5A623',
                    valueFormatter: (value) => {
                        const d = data.find(d => d.profit === value);
                        const rev = d?.revenue;
                        const pct = rev ? ((value / rev) * 100).toFixed(1) + '%' : '';
                        return currencyToText(cur, true, fraction.format(value)) + (pct ? ` (${pct})` : '');
                    },
                },
            ]}
            xAxis={[{
                data: data.map(d => d.xAxis),
                scaleType: 'band',
            }]}
            yAxis={[{
                min: 0,
                valueFormatter: (value) => fraction.format(value),
            }]}
            sx={{
                '& .MuiBarElement-root': {
                    rx: 4,
                },
            }}
        />
    );
}

export default QuotationsAnalytics;

function isSent(opportunity) {
    return opportunity.status === 'endcustomer-accepted' || 
        opportunity.status === 'completed' ||
        opportunity.status === 'sent-to-endcustomer' ||
        opportunity.status === 'opened-by-customer' ||
        opportunity.status === 'lost' ||
        opportunity.status === 'endcustomer-rejected';
}

function isAccepted(opportunity) {
    return opportunity.status === 'endcustomer-accepted' ||
        opportunity.status === 'completed';
}